import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, input, ViewEncapsulation } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { uniqueId } from 'lodash-es';
import { Colours } from '../../../../design-system/colours';
import { fadeOutAnimation } from '../../../../functions/animations/fade-out.animation';
import { ReadOnlyDirective } from '../../directives/read-only.directive';

@Component({
  animations: [fadeOutAnimation],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, ReactiveFormsModule, ReadOnlyDirective],
  selector: 'app-input-checkbox',
  standalone: true,
  styleUrls: ['./input-checkbox.component.scss'],
  template: `
    <input
      appReadOnly
      type="checkbox"
      [class.small]="small()"
      [checked]="control().value"
      [disabled]="disabled()"
      [formControl]="control()"
      [id]="id()"
      [ignoreReadOnly]="ignoreReadOnly()"
      [name]="id()"
    />
    @if (control().value) {
      @if (small()) {
        <svg
          [@fadeOut]
          style="left: 1px;"
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
        >
          <path
            d="M4.73412 11.7155C4.6593 11.713 4.58579 11.6952 4.51809 11.6633C4.45039 11.6314 4.38993 11.586 4.3404 11.5298L0.626117 7.81554C0.555299 7.76962 0.495838 7.70822 0.452224 7.63596C0.40861 7.5637 0.381982 7.48248 0.374352 7.39843C0.366722 7.31437 0.378288 7.22968 0.408177 7.15075C0.438067 7.07182 0.485498 7.00071 0.54689 6.94279C0.608283 6.88488 0.682031 6.84166 0.762568 6.81642C0.843104 6.79117 0.928324 6.78456 1.01179 6.79707C1.09526 6.80958 1.1748 6.84089 1.24439 6.88863C1.31399 6.93638 1.37183 6.99931 1.41355 7.07268L4.73412 10.371L12.5118 2.61554C12.6165 2.54765 12.741 2.51688 12.8653 2.52816C12.9896 2.53944 13.1065 2.59212 13.1972 2.67776C13.288 2.7634 13.3474 2.87703 13.3659 3.00044C13.3844 3.12386 13.3609 3.24991 13.2993 3.3584L5.12783 11.5298C5.07831 11.586 5.01784 11.6314 4.95014 11.6633C4.88244 11.6952 4.80893 11.713 4.73412 11.7155Z"
            fill="white"
          />
        </svg>
      } @else {
        <svg
          [@fadeOut]
          style="left: 1px;"
          xmlns="http://www.w3.org/2000/svg"
          width="22"
          height="22"
          viewBox="0 0 22 22"
          fill="none"
        >
          <path
            d="M8.13265 16.8398C8.04034 16.8367 7.94963 16.8148 7.86609 16.7754C7.78254 16.736 7.70793 16.6799 7.64682 16.6106L3.06349 12.0273C2.9761 11.9706 2.90273 11.8949 2.84891 11.8057C2.79509 11.7165 2.76223 11.6163 2.75282 11.5126C2.7434 11.4089 2.75767 11.3044 2.79456 11.207C2.83144 11.1096 2.88997 11.0218 2.96572 10.9504C3.04148 10.8789 3.13248 10.8256 3.23186 10.7944C3.33124 10.7633 3.4364 10.7551 3.5394 10.7705C3.6424 10.786 3.74054 10.8246 3.82642 10.8835C3.9123 10.9424 3.98368 11.0201 4.03515 11.1106L8.13265 15.1806L17.7302 5.61064C17.8594 5.52686 18.013 5.48889 18.1663 5.50282C18.3197 5.51674 18.4639 5.58175 18.5759 5.68742C18.688 5.79309 18.7612 5.93331 18.7841 6.0856C18.8069 6.23789 18.7779 6.39344 18.7018 6.52731L8.61849 16.6106C8.55737 16.6799 8.48276 16.736 8.39922 16.7754C8.31568 16.8148 8.22497 16.8367 8.13265 16.8398Z"
            fill="white"
          />
        </svg>
      }
    }
    <label [for]="id()" [style.color]="optionLabelColour()">
      @if (label()) {
        {{ label() }}
      } @else {
        <ng-content />
      }
    </label>
  `,
})
export class InputCheckboxComponent {
  control = input.required<FormControl<boolean>>();
  disabled = input<boolean>(false);
  id = input<string>(uniqueId('input-checkbox-'));
  ignoreReadOnly = input<boolean>(false);
  label = input<string>();
  small = input<boolean>(false);

  optionLabelColour = computed(() => (this.disabled() ? Colours.grey200 : Colours.textPrimary));
}
